(() => {
    'use strict';

    const HERO_SELECTOR = '[data-hero]';
    const SWIPE_THRESHOLD = 48;
    const POINTER_CANCEL_THRESHOLD = 14;

    const instances = new WeakMap();

    class HeroSlider {
        constructor(root) {
            this.root = root;
            this.slides = [...root.querySelectorAll('[data-hero-slide]')];
            this.paginationButtons = [
                ...root.querySelectorAll('[data-hero-pagination-button]')
            ];

            this.previousButton = root.querySelector('[data-hero-previous]');
            this.nextButton = root.querySelector('[data-hero-next]');
            this.autoplayButton = root.querySelector('[data-hero-autoplay-toggle]');

            this.abortController = new AbortController();

            this.currentIndex = Math.max(
                0,
                this.slides.findIndex((slide) => slide.classList.contains('is-active'))
            );

            this.autoplayEnabled = root.dataset.autoplay === 'true';
            this.autoplaySpeed = Math.max(
                4000,
                Number(root.dataset.autoplaySpeed) || 6000
            );

            this.autoplayTimer = null;
            this.manuallyPaused = false;
            this.interactionPaused = false;
            this.editorPaused = false;

            this.pointerStartX = null;
            this.pointerStartY = null;
            this.pointerId = null;
            this.pointerMoved = false;

            this.reducedMotionQuery = window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

            this.init();
        }

        init() {
            if (!this.slides.length) {
                return;
            }

            this.bindEvents();
            this.syncSlides({ announce: false, force: true });
            this.syncVideos();
            this.startAutoplay();
        }

        bindEvents() {
            const signal = this.abortController.signal;

            this.previousButton?.addEventListener(
                'click',
                () => this.previous({ userInitiated: true }),
                { signal }
            );

            this.nextButton?.addEventListener(
                'click',
                () => this.next({ userInitiated: true }),
                { signal }
            );

            this.paginationButtons.forEach((button) => {
                button.addEventListener(
                    'click',
                    () => {
                        const index = Number(button.dataset.slideIndex);

                        if (!Number.isFinite(index)) {
                            return;
                        }

                        this.goTo(index, { userInitiated: true });
                    },
                    { signal }
                );
            });

            this.autoplayButton?.addEventListener(
                'click',
                () => this.toggleManualPause(),
                { signal }
            );

            this.root.addEventListener(
                'pointerenter',
                () => this.setInteractionPaused(true),
                { signal }
            );

            this.root.addEventListener(
                'pointerleave',
                () => this.setInteractionPaused(false),
                { signal }
            );

            this.root.addEventListener(
                'focusin',
                () => this.setInteractionPaused(true),
                { signal }
            );

            this.root.addEventListener(
                'focusout',
                (event) => {
                    const nextFocused = event.relatedTarget;

                    if (
                        nextFocused instanceof Node &&
                        this.root.contains(nextFocused)
                    ) {
                        return;
                    }

                    this.setInteractionPaused(false);
                },
                { signal }
            );

            this.root.addEventListener(
                'keydown',
                (event) => this.handleKeydown(event),
                { signal }
            );

            this.root.addEventListener(
                'pointerdown',
                (event) => this.handlePointerDown(event),
                { signal }
            );

            this.root.addEventListener(
                'pointermove',
                (event) => this.handlePointerMove(event),
                { signal }
            );

            this.root.addEventListener(
                'pointerup',
                (event) => this.handlePointerEnd(event),
                { signal }
            );

            this.root.addEventListener(
                'pointercancel',
                () => this.resetPointer(),
                { signal }
            );

            document.addEventListener(
                'visibilitychange',
                () => {
                    if (document.hidden) {
                        this.stopAutoplay();
                    } else {
                        this.startAutoplay();
                    }
                },
                { signal }
            );

            const onReducedMotionChange = () => {
                this.stopAutoplay();
                this.resetProgress();

                if (!this.reducedMotionQuery.matches) {
                    this.startAutoplay();
                }
            };

            if (typeof this.reducedMotionQuery.addEventListener === 'function') {
                this.reducedMotionQuery.addEventListener(
                    'change',
                    onReducedMotionChange,
                    { signal }
                );
            }

            document.addEventListener(
                'shopify:block:select',
                (event) => this.handleBlockSelect(event),
                { signal }
            );

            document.addEventListener(
                'shopify:block:deselect',
                (event) => this.handleBlockDeselect(event),
                { signal }
            );
        }

        handleKeydown(event) {
            if (event.defaultPrevented) {
                return;
            }

            const target = event.target;

            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement
            ) {
                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                this.previous({ userInitiated: true });
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                this.next({ userInitiated: true });
            }
        }

        handlePointerDown(event) {
            if (event.pointerType === 'mouse' && event.button !== 0) {
                return;
            }

            if (
                event.target.closest(
                    'a, button, input, select, textarea, [role="button"]'
                )
            ) {
                return;
            }

            this.pointerId = event.pointerId;
            this.pointerStartX = event.clientX;
            this.pointerStartY = event.clientY;
            this.pointerMoved = false;
        }

        handlePointerMove(event) {
            if (
                this.pointerId === null ||
                event.pointerId !== this.pointerId ||
                this.pointerStartX === null ||
                this.pointerStartY === null
            ) {
                return;
            }

            const deltaX = Math.abs(event.clientX - this.pointerStartX);
            const deltaY = Math.abs(event.clientY - this.pointerStartY);

            if (
                deltaX > POINTER_CANCEL_THRESHOLD ||
                deltaY > POINTER_CANCEL_THRESHOLD
            ) {
                this.pointerMoved = true;
            }
        }

        handlePointerEnd(event) {
            if (
                this.pointerId === null ||
                event.pointerId !== this.pointerId ||
                this.pointerStartX === null ||
                this.pointerStartY === null
            ) {
                this.resetPointer();
                return;
            }

            const deltaX = event.clientX - this.pointerStartX;
            const deltaY = event.clientY - this.pointerStartY;

            const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY) * 1.15;
            const passedThreshold = Math.abs(deltaX) >= SWIPE_THRESHOLD;

            if (this.pointerMoved && isHorizontal && passedThreshold) {
                if (deltaX < 0) {
                    this.next({ userInitiated: true });
                } else {
                    this.previous({ userInitiated: true });
                }
            }

            this.resetPointer();
        }

        resetPointer() {
            this.pointerStartX = null;
            this.pointerStartY = null;
            this.pointerId = null;
            this.pointerMoved = false;
        }

        normalizeIndex(index) {
            const count = this.slides.length;

            if (!count) {
                return 0;
            }

            return ((index % count) + count) % count;
        }

        goTo(index, { userInitiated = false, announce = true } = {}) {
            if (this.slides.length <= 1) {
                return;
            }

            const nextIndex = this.normalizeIndex(index);

            if (nextIndex === this.currentIndex) {
                if (userInitiated) {
                    this.restartAutoplay();
                }

                return;
            }

            this.currentIndex = nextIndex;

            this.syncSlides({ announce });
            this.syncVideos();

            if (userInitiated) {
                this.restartAutoplay();
            } else {
                this.startAutoplay();
            }
        }

        previous(options = {}) {
            this.goTo(this.currentIndex - 1, options);
        }

        next(options = {}) {
            this.goTo(this.currentIndex + 1, options);
        }

        syncSlides({ announce = true, force = false } = {}) {
            this.slides.forEach((slide, index) => {
                const active = index === this.currentIndex;

                slide.classList.toggle('is-active', active);
                slide.hidden = !active;
                slide.setAttribute('aria-hidden', String(!active));

                if ('inert' in HTMLElement.prototype) {
                    slide.inert = !active;
                }
            });

            this.paginationButtons.forEach((button, index) => {
                const active = index === this.currentIndex;

                button.classList.toggle('is-active', active);
                button.setAttribute('aria-current', active ? 'true' : 'false');
            });

            this.animateProgress();

            if (announce || force) {
                this.root.dataset.activeSlide = String(this.currentIndex);
            }

            if (announce) {
                this.root.dispatchEvent(
                    new CustomEvent('theme:hero:change', {
                        bubbles: true,
                        detail: {
                            index: this.currentIndex,
                            slide: this.slides[this.currentIndex]
                        }
                    })
                );
            }
        }

        syncVideos() {
            this.slides.forEach((slide, index) => {
                const video = slide.querySelector('video');

                if (!video) {
                    return;
                }

                if (index !== this.currentIndex) {
                    video.pause();
                    return;
                }

                if (this.reducedMotionQuery.matches) {
                    video.pause();
                    return;
                }

                const playPromise = video.play();

                if (playPromise?.catch) {
                    playPromise.catch(() => {
                        // Autoplay may be blocked by the browser.
                    });
                }
            });
        }

        shouldAutoplay() {
            return (
                this.autoplayEnabled &&
                this.slides.length > 1 &&
                !this.manuallyPaused &&
                !this.interactionPaused &&
                !this.editorPaused &&
                !document.hidden &&
                !this.reducedMotionQuery.matches
            );
        }

        startAutoplay() {
            this.stopAutoplay({ preserveProgress: true });

            if (!this.shouldAutoplay()) {
                this.resetProgress();
                return;
            }

            this.animateProgress();

            this.autoplayTimer = window.setTimeout(() => {
                this.autoplayTimer = null;
                this.next({ userInitiated: false });
            }, this.autoplaySpeed);
        }

        stopAutoplay({ preserveProgress = false } = {}) {
            if (this.autoplayTimer !== null) {
                window.clearTimeout(this.autoplayTimer);
                this.autoplayTimer = null;
            }

            if (!preserveProgress) {
                this.resetProgress();
            }
        }

        restartAutoplay() {
            this.stopAutoplay();
            this.startAutoplay();
        }

        setInteractionPaused(paused) {
            if (this.interactionPaused === paused) {
                return;
            }

            this.interactionPaused = paused;

            if (paused) {
                this.stopAutoplay();
            } else {
                this.startAutoplay();
            }
        }

        toggleManualPause() {
            this.manuallyPaused = !this.manuallyPaused;
            this.syncAutoplayButton();

            if (this.manuallyPaused) {
                this.stopAutoplay();
            } else {
                this.startAutoplay();
            }
        }

        syncAutoplayButton() {
            if (!this.autoplayButton) {
                return;
            }

            this.autoplayButton.setAttribute(
                'aria-pressed',
                String(this.manuallyPaused)
            );

            this.autoplayButton.setAttribute(
                'aria-label',
                this.manuallyPaused ? 'Play slideshow' : 'Pause slideshow'
            );
        }

        resetProgress() {
            this.paginationButtons.forEach((button) => {
                const progress = button.querySelector('.hero__pagination-progress');

                if (!progress) {
                    return;
                }

                progress.style.transition = 'none';
                progress.style.width = button.classList.contains('is-active')
                    ? '100%'
                    : '0%';
            });
        }

        animateProgress() {
            this.paginationButtons.forEach((button, index) => {
                const progress = button.querySelector('.hero__pagination-progress');

                if (!progress) {
                    return;
                }

                progress.style.transition = 'none';
                progress.style.width = '0%';

                if (index !== this.currentIndex) {
                    return;
                }

                if (!this.shouldAutoplay()) {
                    progress.style.width = '100%';
                    return;
                }

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        progress.style.transition = `width ${this.autoplaySpeed}ms linear`;
                        progress.style.width = '100%';
                    });
                });
            });
        }

        handleBlockSelect(event) {
            const block = event.target?.closest?.('[data-hero-slide]');

            if (!block || !this.root.contains(block)) {
                return;
            }

            const index = this.slides.indexOf(block);

            if (index < 0) {
                return;
            }

            this.editorPaused = true;
            this.stopAutoplay();

            this.goTo(index, {
                announce: false
            });
        }

        handleBlockDeselect(event) {
            const block = event.target?.closest?.('[data-hero-slide]');

            if (!block || !this.root.contains(block)) {
                return;
            }

            this.editorPaused = false;
            this.startAutoplay();
        }

        destroy() {
            this.stopAutoplay();

            this.abortController.abort();

            this.resetPointer();

            this.slides.forEach((slide) => {
                if ('inert' in HTMLElement.prototype) {
                    slide.inert = false;
                }
            });
        }
    }

    const initializeHero = (root) => {
        if (
            !(root instanceof HTMLElement) ||
            !root.matches(HERO_SELECTOR)
        ) {
            return;
        }

        instances.get(root)?.destroy();

        const instance = new HeroSlider(root);

        instances.set(root, instance);
    };

    const initializeAllHeroes = (scope = document) => {
        scope
            .querySelectorAll(HERO_SELECTOR)
            .forEach(initializeHero);
    };

    if (document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            () => initializeAllHeroes(),
            {
                once: true
            }
        );
    } else {
        initializeAllHeroes();
    }

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            if (event.target.matches?.(HERO_SELECTOR)) {
                initializeHero(event.target);
                return;
            }

            initializeAllHeroes(event.target);
        }
    );

    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            const root =
                event.target.matches?.(HERO_SELECTOR)
                    ? event.target
                    : event.target.querySelector?.(HERO_SELECTOR);

            if (!root) {
                return;
            }

            instances.get(root)?.destroy();
            instances.delete(root);
        }
    );
})();